import { useEffect, useState, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { toast } from "sonner";
import api, { API } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { MagnifyingGlass, Trash, DownloadSimple, Eye, NotePencil } from "@phosphor-icons/react";

const STATUS_SETS = {
  default: ["New", "Contacted", "Pending", "Scheduled", "Completed", "Closed"],
  "odp-referrals": ["New", "Contacted", "Eligibility/Authorization Review", "Pending Start", "Active", "Closed"],
  applications: ["New", "Under Review", "Contacted", "Interview Scheduled", "Offered", "Hired", "Not Selected"],
};
const statusColor = {
  New: "bg-brand-purple/10 text-brand-purple", Contacted: "bg-blue-100 text-blue-700",
  Pending: "bg-orange-100 text-orange-700", Scheduled: "bg-amber-100 text-amber-700",
  Completed: "bg-emerald-100 text-emerald-700", Closed: "bg-slate-200 text-slate-600",
  "Eligibility/Authorization Review": "bg-indigo-100 text-indigo-700",
  "Pending Start": "bg-amber-100 text-amber-700", Active: "bg-emerald-100 text-emerald-700",
  "Under Review": "bg-blue-100 text-blue-700", "Interview Scheduled": "bg-indigo-100 text-indigo-700",
  Offered: "bg-amber-100 text-amber-700", Hired: "bg-emerald-100 text-emerald-700",
  "Not Selected": "bg-slate-200 text-slate-600",
};

const CONFIG = {
  "ride-requests": {
    title: "Ride Requests",
    columns: [
      ["Passenger", (r) => `${r.first_name} ${r.last_name}`],
      ["Phone", (r) => r.phone],
      ["Date", (r) => r.transportation_date],
      ["Type", (r) => r.transportation_type],
    ],
  },
  quotes: {
    title: "Quote Requests",
    columns: [["Name", (r) => r.name], ["Phone", (r) => r.phone], ["Date", (r) => r.date], ["Type", (r) => r.transportation_type]],
  },
  "facility-leads": {
    title: "Facility Partnership Leads",
    columns: [["Organization", (r) => r.organization_name], ["Contact", (r) => r.contact_name], ["Phone", (r) => r.phone], ["Email", (r) => r.email]],
  },
  contacts: {
    title: "Contact Messages",
    columns: [["Name", (r) => r.name], ["Email", (r) => r.email], ["Subject", (r) => r.subject || "—"]],
  },
  applications: {
    title: "Job Applications",
    columns: [["Applicant", (r) => r.full_name], ["Phone", (r) => r.phone], ["Category", (r) => r.category || "—"], ["Position(s)", (r) => (r.positions && r.positions.length ? r.positions.join(", ") : r.position) || "—"], ["County", (r) => r.county || "—"]],
  },
  "odp-referrals": {
    title: "ODP Referrals",
    columns: [["Individual", (r) => r.individual_name], ["Contact", (r) => r.contact_name], ["Service", (r) => r.requested_service], ["County", (r) => r.county || "—"]],
  },
};

const FIELD_LABELS = {
  first_name: "First Name", last_name: "Last Name", phone: "Phone", email: "Email",
  pickup_address: "Pickup Address", destination_address: "Destination", destination: "Destination",
  transportation_date: "Date", date: "Date", requested_pickup_time: "Pickup Time", pickup_time: "Pickup Time",
  appointment_time: "Appointment Time", transportation_type: "Type", trip_type: "Trip", passengers: "Passengers",
  additional_info: "Additional Info", name: "Name", notes: "Notes", organization_name: "Organization",
  contact_name: "Contact Name", job_title: "Job Title", facility_address: "Facility Address",
  organization_type: "Org Type", trips_per_week: "Trips/Week", transportation_needs: "Needs",
  counties_needed: "Counties", ambulatory_needed: "Ambulatory", wheelchair_needed: "Wheelchair",
  recurring_needed: "Recurring", subject: "Subject", message: "Message", full_name: "Full Name",
  address: "Address", position: "Position", license_status: "License", years_experience: "Experience",
  wheelchair_experience: "Wheelchair Exp", availability: "Availability",
  individual_name: "Individual", contact_name: "Contact Name", relationship: "Relationship",
  county: "County", supports_coordinator: "Supports Coordinator", sco: "Supports Coordination Org",
  requested_service: "Requested Service", preferred_start_date: "Preferred Start Date",
  general_notes: "General Notes",
  category: "Category", positions: "Position(s)", job_title: "Applied For", employment_preference: "FT/PT Preference",
  license_number: "License #", transportation_availability: "Transport to Work",
  odp_experience: "ODP/DSP Experience", certifications: "Certifications/Clearances", acknowledged: "Acknowledged",
};

const HIDDEN = ["id", "status", "created_at", "resume_file_id", "resume_filename", "job_opening_id",
  "internal_notes", "follow_up_date", "follow_up_time", "assigned_to", "priority", "notes_updated_at"];
const PRIORITIES = ["Normal", "Important", "Urgent"];

export default function Leads() {
  const { pathname } = useLocation();
  const type = pathname.split("/").pop();
  const cfg = CONFIG[type];
  const STATUSES = ["All", ...(STATUS_SETS[type] || STATUS_SETS.default)];
  const statusWidth = type === "odp-referrals" ? "w-56" : "w-32";
  const [rows, setRows] = useState([]);
  const [status, setStatus] = useState("All");
  const [q, setQ] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [selected, setSelected] = useState(null);
  const [notes, setNotes] = useState({ assigned_to: "", follow_up_date: "", follow_up_time: "", priority: "Normal", internal_notes: "" });
  const [savingNotes, setSavingNotes] = useState(false);
  const [activity, setActivity] = useState([]);
  const [attempt, setAttempt] = useState("");
  const [catFilter, setCatFilter] = useState("All");
  const [countyFilter, setCountyFilter] = useState("All");
  const [posFilter, setPosFilter] = useState("All");
  const [empFilter, setEmpFilter] = useState("All");

  const loadActivity = async (id) => {
    try { const { data } = await api.get(`/admin/leads/${type}/${id}/activity`); setActivity(data); }
    catch { setActivity([]); }
  };

  useEffect(() => {
    if (selected) {
      setNotes({
        assigned_to: selected.assigned_to || "",
        follow_up_date: selected.follow_up_date || "",
        follow_up_time: selected.follow_up_time || "",
        priority: selected.priority || "Normal",
        internal_notes: selected.internal_notes || "",
      });
      setAttempt("");
      loadActivity(selected.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected]);

  const saveNotes = async () => {
    setSavingNotes(true);
    try {
      await api.patch(`/admin/leads/${type}/${selected.id}/notes`, notes);
      setRows((rs) => rs.map((r) => (r.id === selected.id ? { ...r, ...notes } : r)));
      setSelected((s) => ({ ...s, ...notes }));
      loadActivity(selected.id);
      toast.success("Follow-up & notes saved");
    } catch { toast.error("Could not save notes"); } finally { setSavingNotes(false); }
  };

  const logAttempt = async () => {
    if (!attempt.trim()) return;
    try {
      await api.post(`/admin/leads/${type}/${selected.id}/activity`, { text: attempt, kind: "contact_attempt" });
      setAttempt("");
      loadActivity(selected.id);
      toast.success("Contact attempt logged");
    } catch { toast.error("Could not log attempt"); }
  };

  const fmtTs = (ts) => { try { return new Date(ts).toLocaleString(); } catch { return ts; } };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (status !== "All") params.status = status;
      if (q) params.q = q;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const { data } = await api.get(`/admin/leads/${type}`, { params });
      setRows(data);
    } catch { toast.error("Failed to load"); } finally { setLoading(false); }
  }, [type, status, q, dateFrom, dateTo]);

  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const exportCsv = async () => {
    setExporting(true);
    try {
      const params = {};
      if (status !== "All") params.status = status;
      if (q) params.q = q;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const res = await api.get(`/admin/leads/${type}/export`, { params, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `caredeserved-${type}-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Export downloaded");
    } catch { toast.error("Export failed"); } finally { setExporting(false); }
  };

  const updateStatus = async (id, newStatus) => {
    try {
      await api.patch(`/admin/leads/${type}/${id}`, { status: newStatus });
      setRows((rs) => rs.map((r) => (r.id === id ? { ...r, status: newStatus } : r)));
      if (selected?.id === id) setSelected((s) => ({ ...s, status: newStatus }));
      toast.success("Status updated");
    } catch { toast.error("Update failed"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this record permanently?")) return;
    try {
      await api.delete(`/admin/leads/${type}/${id}`);
      setRows((rs) => rs.filter((r) => r.id !== id));
      setSelected(null);
      toast.success("Deleted");
    } catch { toast.error("Delete failed"); }
  };

  const downloadResume = (fileId, filename) => {
    const token = localStorage.getItem("cd_token") || "";
    window.open(`${API}/admin/resume/${fileId}?auth=${token}`, "_blank");
  };

  if (!cfg) return <p>Unknown lead type.</p>;

  const isApps = type === "applications";
  const countyOpts = ["All", ...Array.from(new Set(rows.map((r) => r.county).filter(Boolean))).sort()];
  const posOpts = ["All", ...Array.from(new Set(rows.flatMap((r) => (r.positions && r.positions.length ? r.positions : (r.position ? [r.position] : []))))).sort()];
  const empOpts = ["All", "Full-Time", "Part-Time", "PRN / As-Needed", "Either"];
  const visibleRows = isApps
    ? rows.filter((r) =>
        (catFilter === "All" || r.category === catFilter) &&
        (countyFilter === "All" || (r.county || "") === countyFilter) &&
        (posFilter === "All" || (r.positions && r.positions.includes(posFilter)) || r.position === posFilter) &&
        (empFilter === "All" || (r.employment_preference || "") === empFilter))
    : rows;

  return (
    <div data-testid={`admin-leads-${type}`}>
      <h1 className="font-heading text-2xl font-bold text-slate-900">{cfg.title}</h1>

      <div className="mt-6 flex flex-col gap-3 lg:flex-row lg:items-end lg:flex-wrap">
        <div className="relative flex-1 lg:min-w-[220px]">
          <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input data-testid="leads-search" placeholder="Search…" value={q} onChange={(e) => setQ(e.target.value)}
            className="h-11 rounded-xl pl-10" />
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-xs font-medium text-slate-500">Status</label>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger data-testid="leads-status-filter" className="h-11 w-full rounded-xl sm:w-44"><SelectValue /></SelectTrigger>
            <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        {isApps && (
          <>
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-slate-500">Category</label>
              <Select value={catFilter} onValueChange={setCatFilter}>
                <SelectTrigger data-testid="apps-category-filter" className="h-11 w-full rounded-xl sm:w-44"><SelectValue /></SelectTrigger>
                <SelectContent>{["All", "Transportation", "ODP", "Transportation & ODP"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-slate-500">County</label>
              <Select value={countyFilter} onValueChange={setCountyFilter}>
                <SelectTrigger data-testid="apps-county-filter" className="h-11 w-full rounded-xl sm:w-40"><SelectValue /></SelectTrigger>
                <SelectContent>{countyOpts.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-slate-500">Position</label>
              <Select value={posFilter} onValueChange={setPosFilter}>
                <SelectTrigger data-testid="apps-position-filter" className="h-11 w-full rounded-xl sm:w-52"><SelectValue /></SelectTrigger>
                <SelectContent>{posOpts.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-slate-500">Type</label>
              <Select value={empFilter} onValueChange={setEmpFilter}>
                <SelectTrigger data-testid="apps-emp-filter" className="h-11 w-full rounded-xl sm:w-40"><SelectValue /></SelectTrigger>
                <SelectContent>{empOpts.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </>
        )}
        <div className="flex flex-col gap-1">
          <label className="text-xs font-medium text-slate-500">From</label>
          <Input data-testid="leads-date-from" type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="h-11 rounded-xl sm:w-40" />
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-xs font-medium text-slate-500">To</label>
          <Input data-testid="leads-date-to" type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="h-11 rounded-xl sm:w-40" />
        </div>
        {(dateFrom || dateTo || q || status !== "All") && (
          <Button variant="ghost" data-testid="leads-clear-filters" onClick={() => { setQ(""); setStatus("All"); setDateFrom(""); setDateTo(""); }}
            className="h-11 rounded-xl text-slate-500">Clear</Button>
        )}
        <Button data-testid="leads-export" onClick={exportCsv} disabled={exporting || rows.length === 0}
          className="h-11 rounded-xl bg-brand-teal font-semibold text-white hover:bg-brand-teal2">
          <DownloadSimple size={18} className="mr-1.5" /> {exporting ? "Exporting…" : "Export CSV"}
        </Button>
      </div>

      <p className="mt-3 text-sm text-slate-500">{visibleRows.length} record{visibleRows.length === 1 ? "" : "s"}</p>

      <div className="mt-6 overflow-hidden rounded-2xl border border-slate-200 bg-white">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
              <tr>
                {cfg.columns.map(([h]) => <th key={h} className="px-4 py-3 font-semibold">{h}</th>)}
                <th className="px-4 py-3 font-semibold">Status</th>
                <th className="px-4 py-3 font-semibold text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading ? (
                <tr><td colSpan={cfg.columns.length + 2} className="px-4 py-10 text-center text-slate-400">Loading…</td></tr>
              ) : visibleRows.length === 0 ? (
                <tr><td colSpan={cfg.columns.length + 2} className="px-4 py-10 text-center text-slate-400">No records found.</td></tr>
              ) : visibleRows.map((r) => (
                <tr key={r.id} className="hover:bg-slate-50" data-testid={`lead-row-${r.id}`}>
                  {cfg.columns.map(([h, fn]) => <td key={h} className="px-4 py-3 text-slate-700">{fn(r)}</td>)}
                  <td className="px-4 py-3">
                    {(r.assigned_to || r.internal_notes || r.follow_up_date) && (
                      <span className="mr-2 inline-flex items-center align-middle" title={r.assigned_to ? `Handled by ${r.assigned_to}` : "Has internal notes"}>
                        <NotePencil size={16} weight="fill" className="text-brand-purple" />
                      </span>
                    )}
                    <Select value={r.status} onValueChange={(v) => updateStatus(r.id, v)}>
                      <SelectTrigger data-testid={`status-${r.id}`} className={`inline-flex h-8 ${statusWidth} whitespace-nowrap rounded-full border-0 text-xs font-semibold ${statusColor[r.status] || ""}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>{STATUSES.slice(1).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                    </Select>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      <button onClick={() => setSelected(r)} data-testid={`view-${r.id}`} className="rounded-lg p-2 text-slate-500 hover:bg-slate-100 hover:text-brand-blue" title="View"><Eye size={18} /></button>
                      {r.resume_file_id && (
                        <button onClick={() => downloadResume(r.resume_file_id, r.resume_filename)} className="rounded-lg p-2 text-slate-500 hover:bg-slate-100 hover:text-brand-teal" title="Download resume"><DownloadSimple size={18} /></button>
                      )}
                      <button onClick={() => remove(r.id)} data-testid={`delete-${r.id}`} className="rounded-lg p-2 text-slate-500 hover:bg-rose-50 hover:text-rose-600" title="Delete"><Trash size={18} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Lead Details</DialogTitle>
            <DialogDescription>Full submission details plus internal, staff-only follow-up tools and activity log.</DialogDescription>
          </DialogHeader>
          {selected && (
            <div className="space-y-2.5">
              {Object.entries(selected).filter(([k, v]) => !HIDDEN.includes(k) && v !== "" && v !== null).map(([k, v]) => (
                <div key={k} className="grid grid-cols-3 gap-3 border-b border-slate-100 pb-2">
                  <span className="text-sm font-semibold text-slate-600">{FIELD_LABELS[k] || k}</span>
                  <span className="col-span-2 text-sm text-slate-800">{typeof v === "boolean" ? (v ? "Yes" : "No") : String(v)}</span>
                </div>
              ))}
              {selected.resume_file_id && (
                <Button onClick={() => downloadResume(selected.resume_file_id, selected.resume_filename)}
                  className="mt-2 rounded-full bg-brand-teal text-white hover:bg-brand-teal2">
                  <DownloadSimple size={18} className="mr-2" /> Download Resume
                </Button>
              )}

              <div className="mt-4 rounded-xl border border-brand-purple/20 bg-brand-purple/[0.04] p-4">
                <div className="flex items-center gap-2">
                  <NotePencil size={18} weight="fill" className="text-brand-purple" />
                  <h4 className="font-heading text-sm font-semibold text-slate-900">Internal Notes</h4>
                  <span className="rounded-full bg-slate-200 px-2 py-0.5 text-[10px] font-semibold text-slate-600">Staff only — not visible to customer</span>
                </div>
                <div className="mt-3 grid gap-3 sm:grid-cols-2">
                  <div className="space-y-1">
                    <Label className="text-xs text-slate-600">Handled by (staff)</Label>
                    <Input data-testid="note-assigned" value={notes.assigned_to}
                      onChange={(e) => setNotes({ ...notes, assigned_to: e.target.value })} placeholder="Staff name" className="h-10 rounded-lg" />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-slate-600">Priority</Label>
                    <Select value={notes.priority} onValueChange={(v) => setNotes({ ...notes, priority: v })}>
                      <SelectTrigger data-testid="note-priority" className="h-10 rounded-lg"><SelectValue /></SelectTrigger>
                      <SelectContent>{PRIORITIES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-slate-600">Follow-up date</Label>
                    <Input data-testid="note-followup" type="date" value={notes.follow_up_date}
                      onChange={(e) => setNotes({ ...notes, follow_up_date: e.target.value })} className="h-10 rounded-lg" />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-slate-600">Follow-up time</Label>
                    <Input data-testid="note-followup-time" type="time" value={notes.follow_up_time}
                      onChange={(e) => setNotes({ ...notes, follow_up_time: e.target.value })} className="h-10 rounded-lg" />
                  </div>
                </div>
                <div className="mt-3 space-y-1">
                  <Label className="text-xs text-slate-600">Follow-up note (private)</Label>
                  <Textarea data-testid="note-text" value={notes.internal_notes}
                    onChange={(e) => setNotes({ ...notes, internal_notes: e.target.value })}
                    placeholder="Internal notes about this request…" className="min-h-[80px] rounded-lg" />
                </div>
                <Button onClick={saveNotes} disabled={savingNotes} data-testid="note-save"
                  className="mt-3 rounded-full bg-brand-purple text-white hover:bg-brand-purple2">
                  {savingNotes ? "Saving…" : "Save Follow-Up & Notes"}
                </Button>
              </div>

              {/* Contact attempt + Activity log */}
              <div className="mt-4 rounded-xl border border-slate-200 bg-white p-4">
                <h4 className="font-heading text-sm font-semibold text-slate-900">Activity Log</h4>
                <div className="mt-2 flex gap-2">
                  <Input data-testid="attempt-text" value={attempt} onChange={(e) => setAttempt(e.target.value)}
                    placeholder="Log a contact attempt or note…" className="h-10 rounded-lg" />
                  <Button data-testid="attempt-log" onClick={logAttempt} variant="outline" className="h-10 shrink-0 rounded-lg border-brand-blue text-brand-blue hover:bg-brand-blue hover:text-white">Log</Button>
                </div>
                <ol className="mt-4 space-y-3 border-l-2 border-slate-100 pl-4" data-testid="activity-timeline">
                  {activity.length === 0 ? (
                    <li className="text-sm text-slate-400">No activity yet.</li>
                  ) : activity.map((a) => (
                    <li key={a.id} className="relative">
                      <span className="absolute -left-[21px] top-1 h-2.5 w-2.5 rounded-full bg-brand-purple" />
                      <p className="text-sm text-slate-800">{a.text}</p>
                      <p className="text-[11px] text-slate-400">{fmtTs(a.ts)}</p>
                    </li>
                  ))}
                </ol>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
